import { Link } from 'react-router-dom';
import { ArrowRight, Camera, CheckCircle2, Gift, HeartHandshake, Home, Palette, Sparkles, Users } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const recipientIdeas = [
  {
    icon: HeartHandshake,
    title: 'Pour les mariés',
    photo: 'Photo de couple, séance engagement, mairie, cérémonie ou souvenir de voyage qui raconte leur histoire.',
    style: 'Peinture à l’huile ou impressionniste pour un rendu premium, aquarelle pour une intention plus douce.',
    source: 'newlyweds',
  },
  {
    icon: Users,
    title: 'Pour les témoins ou la famille',
    photo: 'Photo de groupe lisible, moment complice, portrait familial ou détail symbolique partagé avec les mariés.',
    style: 'Aquarelle ou croquis si le cadeau doit rester élégant, pop art si le couple aime les rendus modernes.',
    source: 'family_witnesses',
  },
  {
    icon: Home,
    title: 'Pour décorer leur intérieur',
    photo: 'Lieu de rencontre, maison, paysage de voyage, animal du couple ou souvenir qui peut vivre dans une pièce.',
    style: 'Impressionniste, huile ou aquarelle selon la décoration et les couleurs déjà présentes chez eux.',
    source: 'home_decor',
  },
];

const weddingChecklist = [
  'Choisissez une photo où le couple ou le souvenir principal se reconnaît immédiatement.',
  'Privilégiez une image émotionnelle et lumineuse plutôt qu’une photo techniquement parfaite mais froide.',
  'Évitez les groupes trop chargés si le tableau doit être compris rapidement au mur.',
  'Gardez le message simple : le cadeau doit valoriser le souvenir, pas ajouter trop de texte dans l’image.',
];

const styleSuggestions = [
  {
    to: '/styles/oil-painting',
    title: 'Peinture à l’huile',
    helper: 'Pour un cadeau de mariage premium, intemporel et facile à exposer dans un salon.',
  },
  {
    to: '/styles/watercolor',
    title: 'Aquarelle',
    helper: 'Pour un rendu doux, lumineux et romantique à partir d’une photo de couple ou de cérémonie.',
  },
  {
    to: '/styles/impressionist',
    title: 'Impressionniste',
    helper: 'Idéal pour transformer un lieu, un voyage ou une scène de mariage en tableau décoratif.',
  },
  {
    to: '/styles/sketch',
    title: 'Croquis',
    helper: 'Une option sobre et élégante quand la photo porte déjà une forte charge émotionnelle.',
  },
];

const planningSteps = [
  {
    title: '1. Partir du lien avec le couple',
    text: 'Le meilleur cadeau de mariage personnalisé dépend de votre place : proche, témoin, parent, ami ou invité.',
  },
  {
    title: '2. Choisir un souvenir qui leur appartient',
    text: 'Une photo de cérémonie fonctionne, mais un lieu commun, un voyage ou un animal du couple peut aussi créer un cadeau très personnel.',
  },
  {
    title: '3. Prévoir l’usage réel du tableau',
    text: 'Un style doux ou premium aide le cadeau à trouver naturellement sa place dans leur intérieur après le mariage.',
  },
];

const relatedPages = [
  {
    to: '/idees-cadeaux-photo-personnalises',
    title: 'Idées cadeaux photo personnalisés',
    helper: 'Comparer mariage, anniversaire, Noël, famille ou animal avant de créer.',
  },
  {
    to: '/cadeau-anniversaire-photo-personnalise',
    title: 'Cadeau anniversaire photo',
    helper: 'Adapter la même logique de cadeau personnel à un anniversaire ou une fête familiale.',
  },
  {
    to: '/blog/cadeau-mariage-original',
    title: 'Guide cadeau mariage original',
    helper: 'Lire le guide éditorial pour comparer les idées avant de lancer la création.',
  },
];

const createPath = (source: string) => `/create?source=wedding_gift_${source}`;

const trackWeddingCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'cadeau-mariage-photo-personnalise',
    source: `wedding_gift_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/cadeau-mariage-photo-personnalise'),
      name: 'Cadeau mariage photo personnalisé',
      description:
        'Guide pour créer un cadeau de mariage photo personnalisé : choix de la photo, style artistique, destinataire et création d’un tableau personnalisé.',
      url: absoluteUrl('/cadeau-mariage-photo-personnalise'),
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl(),
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: absoluteUrl(),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cadeau mariage photo personnalisé',
          item: absoluteUrl('/cadeau-mariage-photo-personnalise'),
        },
      ],
    },
  ],
};

const CadeauMariagePhotoPersonnalise = () => {
  return (
    <>
      <SEOHead
        title="Cadeau mariage photo personnalisé — Tableau unique | Ma Photo en Tableau"
        description="Créez un cadeau mariage photo personnalisé à partir d’un souvenir de couple : conseils photo, styles artistiques, idées par proche et création de tableau personnalisé."
        canonical={absoluteUrl('/cadeau-mariage-photo-personnalise')}
        schema={schema}
      />
      <div className="min-h-screen overflow-x-hidden">
        <main>
          <section className="section-shell gradient-hero relative overflow-hidden pt-32 pb-20">
            <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
            <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
            <div className="container relative mx-auto px-6">
              <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                    Cadeau mariage photo personnalisé
                  </p>
                  <h1 className="display-title mb-6 text-foreground">
                    Offrez un <span className="text-premium">cadeau de mariage personnel</span> à partir d’une photo qui raconte leur histoire.
                  </h1>
                  <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                    Transformez une photo de couple, de cérémonie, de voyage ou de famille en tableau personnalisé. Le parcours aide à choisir un souvenir lisible, un style artistique et un rendu prêt à offrir.
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                      <Link to={createPath('hero_primary')} onClick={() => trackWeddingCta('hero_primary')}>
                        Créer le cadeau de mariage
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                      <Link to="/idees-cadeaux-photo-personnalises">Voir les autres idées cadeaux</Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                  <img
                    src="/site-assets/hero-wedding.jpg"
                    alt="Portrait de couple présenté comme tableau encadré pour un cadeau de mariage"
                    width={800}
                    height={640}
                    className="mb-6 aspect-[5/4] w-full rounded-[1.5rem] object-cover"
                  />
                  <Gift className="mb-5 h-10 w-10 text-primary" />
                  <h2 className="font-display text-4xl leading-none text-foreground">La bonne idée : un souvenir de couple qui reste visible après le mariage.</h2>
                  <div className="mt-6 space-y-4">
                    {weddingChecklist.map((tip) => (
                      <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Choix rapide</p>
                <h2 className="section-title text-foreground">Quel cadeau photo de mariage selon votre lien avec le couple ?</h2>
                <p className="mt-4 text-lg leading-8 text-foreground/65">
                  Une page mariage convertit mieux quand l’intention est claire : offrir aux mariés, remercier des témoins, ou préparer un tableau décoratif pour leur nouveau foyer.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {recipientIdeas.map((idea) => (
                  <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                      <idea.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                    <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                    <Button variant="premium" className="mt-6 rounded-full" asChild>
                      <Link to={createPath(idea.source)} onClick={() => trackWeddingCta(idea.source)}>
                        Créer pour ce cas
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-8 shadow-card backdrop-blur-md">
                  <Sparkles className="mb-5 h-8 w-8 text-primary" />
                  <h2 className="section-title text-foreground">Un cadeau personnel sans tomber dans l’objet impersonnel.</h2>
                  <p className="mt-5 text-sm leading-7 text-foreground/68">
                    Le mariage attire beaucoup d’idées standardisées. Un tableau photo personnalisé permet de partir d’un souvenir réel et de le transformer en objet décoratif simple à comprendre, utile et émotionnel.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {planningSteps.map((step) => (
                    <div key={step.title} className="rounded-[1.5rem] border border-white/65 bg-white/60 p-5 shadow-card">
                      <h3 className="font-display text-2xl leading-none text-foreground">{step.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-foreground/62">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-3">Styles conseillés</p>
                  <h2 className="section-title text-foreground">Quel style artistique pour un cadeau de mariage ?</h2>
                </div>
                <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                  <Link to={createPath('style_section')} onClick={() => trackWeddingCta('style_section')}>Tester avec ma photo</Link>
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {styleSuggestions.map((style) => (
                  <Link key={style.to} to={style.to} className="group rounded-[1.6rem] border border-white/65 bg-white/60 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <Palette className="mb-4 h-6 w-6 text-primary" />
                    <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{style.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/62">{style.helper}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Voir le style
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="grid gap-5 md:grid-cols-3">
                {relatedPages.map((page) => (
                  <Link key={page.to} to={page.to} className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{page.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65">{page.helper}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Continuer le guide
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell pb-24 pt-10">
            <div className="container mx-auto px-6 text-center">
              <div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10">
                <Camera className="mx-auto mb-5 h-8 w-8 text-primary" />
                <h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à créer un cadeau mariage photo ?</h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">
                  Importez une photo de couple ou un souvenir partagé, choisissez un style artistique, puis avancez vers le support qui correspond au cadeau.
                </p>
                <Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild>
                  <Link to={createPath('final_cta')} onClick={() => trackWeddingCta('final_cta')}>
                    Commencer mon cadeau de mariage
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CadeauMariagePhotoPersonnalise;
